import type {
  WashSummary,
  WashSummaryRow,
  WashUnallocatedUtility,
  WashUtilitySource,
} from "@station/contracts";
import { Decimal, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarCheck, ChartColumnBig, Wrench } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChartWithTable } from "../../../features/fuel/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import {
  useWashDayClosings,
  useWashFaults,
  useWashOccupancy,
  useWashSummary,
  useWashUnits,
} from "../../../features/wash/queries.js";
import {
  TableFrame,
  Th,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../features/wash/shared.js";
import {
  DAY_CLOSING_TONE,
  defaultReportRange,
  faultsChartOption,
  formatDuration,
  marginTone,
  occupancyHeatmapOption,
  occupancyRows,
  occupiedHourRange,
  summaryChartOption,
  summaryHasData,
  unallocatedSummaryRow,
} from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../lib/format.js";

const TABS = ["summary", "occupancy", "faults", "closings"] as const;
type TabKey = (typeof TABS)[number];
const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...washSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  by: z.enum(["unit", "program"]).optional().catch(undefined),
  unitId: z.string().uuid().optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/yikama/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ReportsRoute,
});

/**
 * Yıkama raporları (Prompt 18 web, ADR 0018 §16–18; `wash.report.read`): ünite / program bazında adet, ciro (KDV hariç),
 * kimyasal / su / elektrik maliyeti — KAYNAĞIYLA (sayaç ya da tahmini; kimyasal stok defteri ya da teorik) — marj ve marj
 * %; çalışmalara PAYLAŞTIRILAMAYAN sayaç tüketimi (ayrı satır + açıklama: satırlarda yok, toplamlarda var); doluluk ısı
 * haritası (hafta günü × saat); arıza süreleri (ünite × kod); gün kapanışları (fiş bağlantılarıyla). Her grafiğin yanında
 * aynı veriyi veren bir tablo vardır.
 */
function ReportsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage
      title={t("menu.washReports")}
      description={t("wash.reports.description")}
      requirement="reports"
    >
      {(context) => <ReportsBody context={context} />}
    </WashStationPage>
  );
}

function ReportsBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const fallback = defaultReportRange(toIstanbulDate(new Date()));
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const tab: TabKey = search.tab ?? "summary";
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  const units = useWashUnits(context.station.id);

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("wash.reports.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
        {tab === "summary" ? (
          <FormField label={t("wash.reports.groupBy")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.by ?? "program"}
                onChange={(event) => setSearch({ by: event.target.value === "program" ? undefined : "unit" })}
              >
                <option value="program">{t("wash.reports.byProgram")}</option>
                <option value="unit">{t("wash.reports.byUnit")}</option>
              </Select>
            )}
          </FormField>
        ) : tab === "occupancy" || tab === "faults" ? (
          <FormField label={t("wash.fields.unit")} className="w-52">
            {(control) => (
              <Select
                {...control}
                value={search.unitId ?? ""}
                onChange={(event) => setSearch({ unitId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {(units.data ?? []).map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
      </Card>
      <Tabs
        value={tab}
        onValueChange={(next) => setSearch({ tab: next === "summary" ? undefined : (next as TabKey) })}
      >
        <TabsList aria-label={t("wash.reports.tabsLabel")} className="flex-wrap">
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`wash.reports.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="summary" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "summary" ? (
            <SummaryTab stationId={context.station.id} range={range} groupBy={search.by ?? "program"} />
          ) : null}
        </TabsContent>
        <TabsContent value="occupancy" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "occupancy" ? (
            <OccupancyTab stationId={context.station.id} range={range} unitId={search.unitId} />
          ) : null}
        </TabsContent>
        <TabsContent value="faults" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "faults" ? (
            <FaultsTab stationId={context.station.id} range={range} unitId={search.unitId} />
          ) : null}
        </TabsContent>
        <TabsContent value="closings" className="grid min-w-0 gap-4 [&>*]:min-w-0">
          {tab === "closings" ? (
            <ClosingsTab
              stationId={context.station.id}
              range={range}
              canOpenEntries={context.permissions.canReadAccounting}
            />
          ) : null}
        </TabsContent>
      </Tabs>
    </>
  );
}

// ── Özet ─────────────────────────────────────────────────────────────────────
function utilitySourceFor(
  sources: readonly WashUtilitySource[],
  utility: WashUtilitySource["utility"],
  unitId: string | null,
): WashUtilitySource["source"] | null {
  if (unitId === null) return null;
  return (
    sources.find((source) => source.utility === utility && source.unitIds.includes(unitId))?.source ?? null
  );
}

function SourceBadge({ source }: { source: WashUtilitySource["source"] | null }): ReactElement | null {
  const { t } = useTranslation();
  if (source === null) return null;
  return (
    <StatusBadge tone={source === "ESTIMATE" ? "warning" : "info"} className="whitespace-nowrap">
      {source === "ESTIMATE" ? t("wash.reports.estimated") : t("wash.reports.metered")}
    </StatusBadge>
  );
}

function MoneyCell({ value, source }: { value: string; source?: ReactElement | null }): ReactElement {
  return (
    <td className="px-3 py-2 text-right">
      <span className="grid justify-items-end gap-1">
        <MoneyText value={value} colorize />
        {source ?? null}
      </span>
    </td>
  );
}

function SummaryTab({
  stationId,
  range,
  groupBy,
}: {
  stationId: string;
  range: { from: string; to: string };
  groupBy: "unit" | "program";
}): ReactElement {
  const { t } = useTranslation();
  const summary = useWashSummary({ stationId, ...range, groupBy });
  const option = useMemo(
    () =>
      summary.data
        ? summaryChartOption(summary.data.rows, {
            revenue: t("wash.reports.revenue"),
            cost: t("wash.reports.totalCost"),
            margin: t("wash.reports.margin"),
          })
        : null,
    [summary.data, t],
  );
  if (summary.error) return <Alert tone="danger">{errorMessage(summary.error)}</Alert>;
  if (!summary.data || !option) return <SkeletonRows rows={6} />;
  const data: WashSummary = summary.data;
  const byUnit = data.groupBy === "unit";

  return (
    <>
      <Card className="flex flex-wrap items-center gap-2 p-4 text-sm" data-testid="wash-cost-sources">
        <span className="font-medium">{t("wash.reports.chemicalSource")}</span>
        <StatusBadge tone={data.chemicalCostSource === "STOCK_LEDGER" ? "info" : "warning"}>
          {data.chemicalCostSource === "STOCK_LEDGER"
            ? t("wash.reports.stockLedger")
            : t("wash.reports.theoretical")}
        </StatusBadge>
        <span className="ms-2 font-medium">{t("wash.reports.utilitySources")}</span>
        {data.utilitySources.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          data.utilitySources.map((source) => (
            <StatusBadge
              key={`${source.utility}-${source.source}-${source.meterId ?? "none"}`}
              tone={source.source === "ESTIMATE" ? "warning" : "info"}
            >
              {t(`wash.reports.utility.${source.utility}`)} · {t(`wash.reports.source.${source.source}`)}
            </StatusBadge>
          ))
        )}
      </Card>
      {data.warnings.length > 0 ? (
        <Alert tone="warning">
          <ul className="grid gap-1">
            {data.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {!summaryHasData(data) ? (
        <EmptyState
          headingLevel={2}
          icon={<ChartColumnBig />}
          title={t("wash.reports.emptyTitle")}
          description={t("wash.reports.emptyDescription")}
        />
      ) : (
        <>
          <TableFrame label={t("wash.reports.summaryCaption")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{byUnit ? t("wash.fields.unit") : t("wash.fields.program")}</Th>
                <Th align="right">{t("wash.reports.count")}</Th>
                <Th align="right">{t("wash.reports.revenue")}</Th>
                <Th align="right">{t("wash.reports.chemicalCost")}</Th>
                <Th align="right">{t("wash.reports.waterCost")}</Th>
                <Th align="right">{t("wash.reports.electricityCost")}</Th>
                <Th align="right">{t("wash.reports.totalCost")}</Th>
                <Th align="right">{t("wash.reports.margin")}</Th>
                <Th align="right">{t("wash.reports.marginPercent")}</Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <SummaryRow
                  key={row.key ?? "unassigned"}
                  row={row}
                  label={row.key === null ? t("wash.reports.unassigned") : row.label}
                  water={byUnit ? utilitySourceFor(data.utilitySources, "WATER", row.key) : null}
                  electricity={byUnit ? utilitySourceFor(data.utilitySources, "ELECTRICITY", row.key) : null}
                />
              ))}
            </tbody>
            {data.unallocated.length > 0 ? (
              <tbody className="border-t-2 border-dashed" data-testid="wash-unallocated-rows">
                {data.unallocated.map((item) => (
                  <SummaryRow
                    key={`${item.utility}-${item.meterId ?? "none"}`}
                    row={unallocatedSummaryRow(item, unallocatedLabel(t, item))}
                    label={unallocatedLabel(t, item)}
                    countText="—"
                  />
                ))}
              </tbody>
            ) : null}
            <tfoot className="border-t-2 font-semibold">
              <SummaryRow row={{ ...data.totals, key: null, label: "" }} label={t("common.total")} />
            </tfoot>
          </TableFrame>
          <UnallocatedTable items={data.unallocated} />
          {data.rows.length > 0 ? (
            <Card className="min-w-0 p-4">
              <h2 className="mb-2 text-base font-semibold">{t("wash.reports.chartTitle")}</h2>
              <ChartWithTable
                ariaLabel={t("wash.reports.chartAria", {
                  from: formatDate(data.from),
                  to: formatDate(data.to),
                })}
                option={option}
                height={Math.max(240, 64 + data.rows.length * 56)}
                tableLabel={t("wash.reports.chartTable")}
              >
                <table className="w-full text-sm">
                  <caption className="sr-only">{t("wash.reports.chartTable")}</caption>
                  <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <Th>{byUnit ? t("wash.fields.unit") : t("wash.fields.program")}</Th>
                      <Th align="right">{t("wash.reports.revenue")}</Th>
                      <Th align="right">{t("wash.reports.totalCost")}</Th>
                      <Th align="right">{t("wash.reports.margin")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.key ?? "unassigned"} className="border-t">
                        <td className="px-3 py-2">
                          {row.key === null ? t("wash.reports.unassigned") : row.label}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.revenue} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.totalCost} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.margin} colorize />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ChartWithTable>
            </Card>
          ) : null}
          <UtilitySourcesTable sources={data.utilitySources} />
        </>
      )}
    </>
  );
}

function SummaryRow({
  row,
  label,
  water = null,
  electricity = null,
  countText,
}: {
  row: WashSummaryRow;
  label: string;
  water?: WashUtilitySource["source"] | null;
  electricity?: WashUtilitySource["source"] | null;
  /** Adet yerine gösterilecek metin (paylaştırılamayan satırda "—"). */
  countText?: string;
}): ReactElement {
  return (
    <tr className="border-t">
      <th scope="row" className="px-3 py-2 text-left font-medium">
        {label}
      </th>
      <td className="px-3 py-2 text-right tabular">{countText ?? formatCount(row.count)}</td>
      <MoneyCell value={row.revenue} />
      <MoneyCell value={row.chemicalCost} />
      <MoneyCell value={row.waterCost} source={<SourceBadge source={water} />} />
      <MoneyCell value={row.electricityCost} source={<SourceBadge source={electricity} />} />
      <MoneyCell value={row.totalCost} />
      <MoneyCell value={row.margin} />
      <td className="px-3 py-2 text-right">
        {row.marginPercent === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <StatusBadge tone={marginTone(row.marginPercent)} className="tabular">
            %{Decimal.from(row.marginPercent).formatTr(2)}
          </StatusBadge>
        )}
      </td>
    </tr>
  );
}

/** Paylaştırılamayan satırın etiketi: "Paylaştırılamayan elektrik — Yıkama alt sayacı". */
function unallocatedLabel(
  t: ReturnType<typeof useTranslation>["t"],
  item: Pick<WashUnallocatedUtility, "utility" | "meterName">,
): string {
  return t("wash.reports.unallocatedRow", {
    utility: t(`wash.reports.utilityLower.${item.utility}`),
    meter: item.meterName ?? "—",
  });
}

/**
 * Çalışmalara paylaştırılamayan sayaç tüketimi: sayaç, gerekçe (çalışma yok / programların tahmini tüketimi 0), tüketim,
 * birim maliyet, maliyet. Ünite / program satırlarına GİRMEZ, rapor toplamına girer.
 */
function UnallocatedTable({ items }: { items: readonly WashUnallocatedUtility[] }): ReactElement | null {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0" data-testid="wash-unallocated">
      <div className="grid gap-1">
        <h2 className="text-base font-semibold">{t("wash.reports.unallocatedTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("wash.reports.unallocatedHint")}</p>
      </div>
      <TableFrame label={t("wash.reports.unallocatedTitle")}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("wash.reports.utilityLabel")}</Th>
            <Th>{t("wash.reports.meter")}</Th>
            <Th>{t("wash.reports.unallocatedReasonLabel")}</Th>
            <Th align="right">{t("wash.reports.cycles")}</Th>
            <Th align="right">{t("wash.reports.quantity")}</Th>
            <Th align="right">{t("wash.reports.unitCost")}</Th>
            <Th align="right">{t("wash.reports.cost")}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={`${item.utility}-${item.meterId ?? "none"}`} className="border-t">
              <td className="px-3 py-2">{t(`wash.reports.utility.${item.utility}`)}</td>
              <td className="px-3 py-2">
                <span className="grid gap-0.5">
                  {item.meterName ?? "—"}
                  <span className="text-xs text-muted-foreground">
                    {t(`wash.reports.source.${item.source}`)}
                  </span>
                </span>
              </td>
              <td className="px-3 py-2">
                <StatusBadge tone="warning">{t(`wash.reports.unallocatedReason.${item.reason}`)}</StatusBadge>
              </td>
              <td className="px-3 py-2 text-right tabular">{formatCount(item.cycleCount)}</td>
              <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                {Decimal.from(item.quantity).formatTr(3)} {item.utility === "WATER" ? "m³" : "kWh"}
              </td>
              <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                {item.unitCost === null ? (
                  <span className="text-muted-foreground">{t("wash.reports.noUnitCost")}</span>
                ) : (
                  `${Decimal.from(item.unitCost).formatTr(4)} ₺`
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <MoneyText value={item.cost} />
              </td>
            </tr>
          ))}
        </tbody>
      </TableFrame>
    </Card>
  );
}

function UtilitySourcesTable({ sources }: { sources: readonly WashUtilitySource[] }): ReactElement | null {
  const { t } = useTranslation();
  if (sources.length === 0) return null;
  return (
    <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
      <h2 className="text-base font-semibold">{t("wash.reports.sourcesTitle")}</h2>
      <TableFrame label={t("wash.reports.sourcesTitle")}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("wash.reports.utilityLabel")}</Th>
            <Th>{t("wash.reports.sourceLabel")}</Th>
            <Th align="right">{t("wash.reports.cycles")}</Th>
            <Th align="right">{t("wash.reports.quantity")}</Th>
            <Th align="right">{t("wash.reports.unitCost")}</Th>
            <Th align="right">{t("wash.reports.cost")}</Th>
          </tr>
        </thead>
        <tbody>
          {sources.map((source) => (
            <tr key={`${source.utility}-${source.source}-${source.meterId ?? "none"}`} className="border-t">
              <td className="px-3 py-2">{t(`wash.reports.utility.${source.utility}`)}</td>
              <td className="px-3 py-2">
                <span className="grid justify-items-start gap-0.5">
                  {t(`wash.reports.source.${source.source}`)}
                  {source.meterName ? (
                    <span className="text-xs text-muted-foreground">{source.meterName}</span>
                  ) : null}
                  {source.deductedQuantity !== null ? (
                    <span className="text-xs text-muted-foreground">
                      {t("wash.reports.deducted", {
                        quantity: `${Decimal.from(source.deductedQuantity).formatTr(3)} ${
                          source.utility === "WATER" ? "m³" : "kWh"
                        }`,
                      })}
                    </span>
                  ) : null}
                  <SourceBadge source={source.source} />
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular">{formatCount(source.cycleCount)}</td>
              <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                {Decimal.from(source.quantity).formatTr(3)} {source.utility === "WATER" ? "m³" : "kWh"}
              </td>
              <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                {source.unitCost === null ? (
                  <span className="text-muted-foreground">{t("wash.reports.noUnitCost")}</span>
                ) : (
                  `${Decimal.from(source.unitCost).formatTr(4)} ₺`
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <MoneyText value={source.cost} />
              </td>
            </tr>
          ))}
        </tbody>
      </TableFrame>
    </Card>
  );
}

// ── Doluluk ──────────────────────────────────────────────────────────────────
function OccupancyTab({
  stationId,
  range,
  unitId,
}: {
  stationId: string;
  range: { from: string; to: string };
  unitId: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const occupancy = useWashOccupancy({ stationId, ...range, unitId });
  const weekdays = useMemo(() => [1, 2, 3, 4, 5, 6, 7].map((day) => t(`wash.weekdaysLong.${day}`)), [t]);
  const option = useMemo(
    () =>
      occupancy.data
        ? occupancyHeatmapOption(occupancy.data, { weekdays, series: t("wash.reports.occupancy") })
        : null,
    [occupancy.data, weekdays, t],
  );
  if (occupancy.error) return <Alert tone="danger">{errorMessage(occupancy.error)}</Alert>;
  if (!occupancy.data || !option) return <SkeletonRows rows={6} />;
  const rows = occupancyRows(occupancy.data);
  const hours = occupiedHourRange(rows);
  const visibleHours = Array.from(
    { length: hours.to - hours.from + 1 },
    (_unused, index) => hours.from + index,
  );
  const total = rows.reduce((sum, row) => sum + row.totalSeconds, 0);

  return (
    <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t("wash.reports.occupancyTitle")}</h2>
        <span className="text-sm text-muted-foreground">
          {t("wash.reports.occupancySummary", {
            units: occupancy.data.unitCount,
            duration: formatDuration(total),
          })}
        </span>
      </div>
      <ChartWithTable
        ariaLabel={t("wash.reports.occupancyAria", {
          from: formatDate(occupancy.data.from),
          to: formatDate(occupancy.data.to),
        })}
        option={option}
        height={360}
        tableLabel={t("wash.reports.occupancyTable")}
      >
        <table className="w-full text-xs">
          <caption className="sr-only">{t("wash.reports.occupancyTable")}</caption>
          <thead className="bg-muted/60 text-muted-foreground">
            <tr>
              <Th>{t("wash.reports.weekday")}</Th>
              {visibleHours.map((hour) => (
                <Th key={hour} align="right">
                  {String(hour).padStart(2, "0")}
                </Th>
              ))}
              <Th align="right">{t("common.total")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.weekday} className="border-t">
                <th scope="row" className="px-3 py-1.5 text-left font-medium">
                  {weekdays[row.weekday]}
                </th>
                {visibleHours.map((hour) => (
                  <td key={hour} className="px-2 py-1.5 text-right tabular">
                    %{Decimal.from(row.cells[hour]!.percent).formatTr(1)}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right tabular whitespace-nowrap">
                  {formatDuration(row.totalSeconds)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ChartWithTable>
      <p className="text-xs text-muted-foreground">{t("wash.reports.occupancyHint")}</p>
    </Card>
  );
}

// ── Arızalar ─────────────────────────────────────────────────────────────────
function FaultsTab({
  stationId,
  range,
  unitId,
}: {
  stationId: string;
  range: { from: string; to: string };
  unitId: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const faults = useWashFaults({ stationId, ...range, unitId });
  const option = useMemo(
    () => (faults.data ? faultsChartOption(faults.data, t("wash.reports.minutes")) : null),
    [faults.data, t],
  );
  if (faults.error) return <Alert tone="danger">{errorMessage(faults.error)}</Alert>;
  if (!faults.data || !option) return <SkeletonRows rows={5} />;
  const data = faults.data;
  if (data.items.length === 0)
    return (
      <EmptyState
        headingLevel={2}
        icon={<Wrench />}
        title={t("wash.reports.noFaultsTitle")}
        description={t("wash.reports.noFaultsDescription")}
      />
    );
  return (
    <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t("wash.reports.faultsTitle")}</h2>
        <span className="text-sm text-muted-foreground">
          {t("wash.reports.faultsTotal", { duration: formatDuration(data.totalSeconds) })}
        </span>
      </div>
      <ChartWithTable
        ariaLabel={t("wash.reports.faultsAria", { from: formatDate(data.from), to: formatDate(data.to) })}
        option={option}
        height={Math.max(220, 64 + data.items.length * 48)}
        tableLabel={t("wash.reports.faultsTable")}
      >
        <table className="w-full text-sm">
          <caption className="sr-only">{t("wash.reports.faultsTable")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("wash.fields.unit")}</Th>
              <Th>{t("wash.reports.faultCode")}</Th>
              <Th align="right">{t("wash.reports.faultCount")}</Th>
              <Th align="right">{t("wash.fields.duration")}</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.flatMap((item) =>
              item.byCode.map((entry) => (
                <tr key={`${item.unitId}-${entry.faultCode}`} className="border-t">
                  <td className="px-3 py-2">{item.unitName}</td>
                  <td className="px-3 py-2 font-mono">{entry.faultCode}</td>
                  <td className="px-3 py-2 text-right tabular">{formatCount(entry.count)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatDuration(entry.seconds)}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </ChartWithTable>
      <TableFrame label={t("wash.reports.faultsByUnit")}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("wash.fields.unit")}</Th>
            <Th align="right">{t("wash.reports.faultCount")}</Th>
            <Th align="right">{t("wash.fields.duration")}</Th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <tr key={item.unitId} className="border-t">
              <th scope="row" className="px-3 py-2 text-left font-medium">
                {item.unitName}
              </th>
              <td className="px-3 py-2 text-right tabular">{formatCount(item.count)}</td>
              <td className="px-3 py-2 text-right tabular">{formatDuration(item.totalSeconds)}</td>
            </tr>
          ))}
        </tbody>
      </TableFrame>
    </Card>
  );
}

// ── Gün kapanışları ──────────────────────────────────────────────────────────
function JournalLink({
  id,
  label,
  enabled,
}: {
  id: string | null;
  label: string;
  enabled: boolean;
}): ReactElement {
  if (id === null) return <span className="text-muted-foreground">—</span>;
  if (!enabled) return <span>{label}</span>;
  return (
    <Link to="/muhasebe/fisler/$id" params={{ id }} className="underline underline-offset-4">
      {label}
    </Link>
  );
}

function ClosingsTab({
  stationId,
  range,
  canOpenEntries,
}: {
  stationId: string;
  range: { from: string; to: string };
  canOpenEntries: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const closings = useWashDayClosings({ stationId, ...range });
  if (closings.error) return <Alert tone="danger">{errorMessage(closings.error)}</Alert>;
  if (!closings.data) return <SkeletonRows rows={5} />;
  if (closings.data.length === 0)
    return (
      <EmptyState
        headingLevel={2}
        icon={<CalendarCheck />}
        title={t("wash.reports.noClosingsTitle")}
        description={t("wash.reports.noClosingsDescription")}
      />
    );
  return (
    <TableFrame label={t("wash.reports.closingsCaption")}>
      <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
        <tr>
          <Th>{t("wash.reports.businessDate")}</Th>
          <Th>{t("common.status")}</Th>
          <Th align="right">{t("wash.reports.cycles")}</Th>
          <Th align="right">{t("wash.reports.consumptionValue")}</Th>
          <Th align="right">{t("wash.reports.machineRevenue")}</Th>
          <Th>{t("wash.reports.journals")}</Th>
          <Th>{t("wash.reports.closedAt")}</Th>
        </tr>
      </thead>
      <tbody>
        {closings.data.map((closing) => (
          <tr key={closing.id} className="border-t align-top">
            <th scope="row" className="px-3 py-2 text-left font-medium tabular">
              {formatDate(closing.businessDate)}
            </th>
            <td className="px-3 py-2">
              <span className="grid gap-1">
                <StatusBadge tone={DAY_CLOSING_TONE[closing.status]}>
                  {t(`wash.reports.closingStatus.${closing.status}`)}
                </StatusBadge>
                {closing.warnings.map((warning) => (
                  <span key={warning} className="text-xs text-warning">
                    {warning}
                  </span>
                ))}
              </span>
            </td>
            <td className="px-3 py-2 text-right tabular">{formatCount(closing.cycleCount)}</td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={closing.consumptionValue} />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={closing.machineRevenueGross} />
            </td>
            <td className="px-3 py-2 text-xs">
              <span className="grid gap-0.5">
                <JournalLink
                  id={closing.consumptionJournalEntryId}
                  label={t("wash.reports.consumptionEntry")}
                  enabled={canOpenEntries}
                />
                <JournalLink
                  id={closing.revenueJournalEntryId}
                  label={t("wash.reports.revenueEntry")}
                  enabled={canOpenEntries}
                />
              </span>
            </td>
            <td className="px-3 py-2 tabular whitespace-nowrap">{formatDateTime(closing.closedAt)}</td>
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
}
